import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type CoverageInsuranceQuery,
  InsuranceCheckState,
  InsuranceStatus,
} from "@medbillai/generated-graphql-types";

import { analyticsService } from "../../../lib/analytics";
import type { CoverageSetupOutcome } from "../../../lib/analytics/types";
import { captureUnreportedError } from "../../../lib/apollo/error";
import { checkOutcome, known } from "../../../lib/insurance/checkOutcome";
import { useInsuranceCheckPoll } from "../../../lib/insurance/useInsuranceCheckPoll";
import {
  recordIdentityMutation,
  recordInsuranceStatusMutation,
} from "../../onboarding-v3/utils/queries";
import { useAcceptTerms } from "../../onboarding-v3/utils/useAcceptTerms";
import { useRecordCarrier } from "../../onboarding-v3/utils/useRecordCarrier";
import { coverageInsuranceQuery } from "../read/queries";
import { coverageSetupIdentityQuery } from "../setup/queries";

export type Insurer = {
  carrier: string;
  /** Null for an insurer named by hand: no payer to check against. */
  payerId: string | null;
  logoUrl: string | null;
  memberIdRequired: boolean;
};

export type Name = { firstName: string; lastName: string };

/** What the setup form submits. `name` is set only when it was asked. */
export type SetupDetails = {
  name: Name | null;
  dateOfBirth: string;
  insurer: Insurer;
  memberId?: string;
};

type Phase = "idle" | "savingUninsured" | "savingPlan" | "polling";

/** Where the setup sheet opens: the form, or its details to check them. */
export type SheetStart = "form" | "profile";

/**
 * Setup runs from Coverage's root, which owns them: the root keeps the setup
 * card through the CHECKING read its own poll causes, until the run resolves.
 */
export function usePlanSetup() {
  // `opened` remounts the sheet's form, so each open starts afresh.
  const [sheet, setSheet] = useState({
    open: false,
    start: "form" as SheetStart,
    opened: 0,
  });
  /**
   * `plan` prefills the insurer of the plan being verified; without one the
   * form starts blank. A running check keeps its insurer for the checking copy.
   */
  const openSheet = (start: SheetStart = "form", plan?: Insurer) => {
    if (plan || phase === "idle") {
      setInsurer(plan ?? null);
    }
    setSheet(s => ({ open: true, start, opened: s.opened + 1 }));
  };
  const setSheetOpen = useCallback(
    (open: boolean) => setSheet(s => ({ ...s, open })),
    [],
  );
  // Fetched up front, so the sheet opens on the form rather than empty.
  const { data } = useQuery(coverageSetupIdentityQuery);
  const { data: before } = useQuery(coverageInsuranceQuery);
  const person = data?.me?.primaryPerson;
  const onFileDob = person?.dateOfBirth || null;
  const [insurer, setInsurer] = useState<Insurer | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [failed, setFailed] = useState(false);
  const { requestAcceptance } = useAcceptTerms();
  const run = useResolveSetup();
  const [recordIdentity] = useMutation(recordIdentityMutation);
  const [recordInsuranceStatus] = useMutation(recordInsuranceStatusMutation);
  const recordCarrier = useRecordCarrier(undefined);
  const onFileName =
    person?.givenNames?.join("") && person.familyName
      ? { firstName: person.givenNames.join(" "), lastName: person.familyName }
      : null;
  const writesIdentity = ({
    name,
    dateOfBirth,
  }: Pick<SetupDetails, "name" | "dateOfBirth">) =>
    !onFileName || !onFileDob || dateOfBirth !== onFileDob || Boolean(name);
  /** Terms are asked when identity is written and they were never accepted. */
  const needsTerms = (details: Pick<SetupDetails, "name" | "dateOfBirth">) =>
    data !== undefined && !data.me?.acceptedTermsAt && writesIdentity(details);

  /** True once the details are saved, whether or not a check goes out. */
  const check = async (details: SetupDetails) => {
    const { dateOfBirth, insurer: picked, memberId } = details;
    const name = details.name ?? onFileName;
    if (phase !== "idle" || !name) {
      return false;
    }
    const identity = { ...name, dateOfBirth };
    run.start();
    setInsurer(picked);
    setPhase("savingPlan");
    setFailed(false);
    if (needsTerms(details) && !(await requestAcceptance())) {
      setPhase("idle");
      setFailed(true);
      return false;
    }
    try {
      if (writesIdentity(details)) {
        await recordIdentity({ variables: { input: identity } });
      }
      await recordCarrier(picked.carrier, picked.payerId, memberId, identity);
    } catch (error) {
      captureUnreportedError(error);
      setPhase("idle");
      setFailed(true);
      return false;
    }
    if (picked.payerId === null) {
      resolve("noPayer");
      return true;
    }
    setPhase("polling");
    return true;
  };

  const resolve = (outcome: CoverageSetupOutcome) => {
    setPhase("idle");
    return run.resolve(outcome);
  };

  const uninsured = () => {
    if (phase !== "idle") {
      return;
    }
    run.start();
    setPhase("savingUninsured");
    recordInsuranceStatus({
      variables: { input: { status: InsuranceStatus.UNINSURED } },
    })
      .then(() => resolve("uninsured"))
      .catch(error => {
        captureUnreportedError(error);
        setPhase("idle");
        setFailed(true);
      });
  };

  return {
    loaded: data !== undefined,
    answeredBefore: before?.myInsurance.checkStatus.lastAnsweredAt ?? null,
    attemptedBefore: before?.myInsurance.checkStatus.lastAttemptAt ?? null,
    dateOfBirth: onFileDob,
    name: onFileName,
    needsTerms,
    insurer,
    setInsurer,
    saving: phase !== "idle",
    failed,
    checking: phase === "savingPlan" || phase === "polling",
    polling: phase === "polling",
    check,
    uninsured,
    resolve,
    sheet,
    openSheet,
    setSheetOpen,
  };
}

export type PlanSetup = ReturnType<typeof usePlanSetup>;

// A re-check starts ~5 s after the details change and takes up to ~17 s.
const CHECK_WINDOW_MS = 25_000;

const timeOf = (d: Date | null | undefined) => d?.getTime() ?? 0;

export type PlanCheckProps = {
  answeredBefore: Date | null;
  attemptedBefore: Date | null;
  windowMs?: number;
  intervalMs?: number;
  onDone: (outcome: CoverageSetupOutcome) => void;
};

/**
 * Waits for a payer answer newer than `answeredBefore`, or a miss on an
 * attempt newer than `attemptedBefore`: a plan added to a checked account is
 * CHECKED from the first poll, on the old answer.
 */
export function usePlanCheck({
  answeredBefore,
  attemptedBefore,
  windowMs = CHECK_WINDOW_MS,
  intervalMs,
  onDone,
}: PlanCheckProps) {
  // Frozen: the poll writes the query the caller read this from.
  const [baseline] = useState({
    answered: timeOf(answeredBefore),
    attempted: timeOf(attemptedBefore),
  });
  const outcomeOf = (d: CoverageInsuranceQuery) => {
    const { checkStatus } = d.myInsurance;
    const state = checkOutcome(known(InsuranceCheckState, checkStatus.state));
    // An outcome from before this run is still a check in flight.
    const fresh =
      state === "checked"
        ? timeOf(checkStatus.lastAnsweredAt) > baseline.answered
        : timeOf(checkStatus.lastAttemptAt) > baseline.attempted;
    return fresh ? state : "pending";
  };
  const { data } = useInsuranceCheckPoll(coverageInsuranceQuery, {
    polling: true,
    intervalMs,
    landed: d => outcomeOf(d) !== "pending",
  });
  const outcome = data ? outcomeOf(data) : "pending";
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  });

  useEffect(() => {
    if (outcome !== "pending") {
      onDoneRef.current(outcome);
    }
  }, [outcome]);
  // A run that already landed reported its outcome; `onDone` reports once.
  useEffect(() => {
    const timer = setTimeout(() => onDoneRef.current("timedOut"), windowMs);
    return () => clearTimeout(timer);
  }, [windowMs]);
}

/**
 * Reports a setup run's outcome once; true the first time only. A run starts
 * on mount, or again on `start` for a caller that outlives one run.
 */
export function useResolveSetup() {
  const client = useApolloClient();
  const startedAt = useRef(Date.now());
  const finished = useRef(false);
  const start = () => {
    startedAt.current = Date.now();
    finished.current = false;
  };
  const resolve = (outcome: CoverageSetupOutcome) => {
    if (finished.current) {
      return false;
    }
    finished.current = true;
    analyticsService.trackEvent("CoverageSetupResolved", {
      outcome,
      elapsedMs: Date.now() - startedAt.current,
    });
    if (outcome === "uninsured") {
      // A worker carries the answer into the read, so a refetch now would
      // still say NOT_STARTED and bring the setup card back.
      client.cache.updateQuery({ query: coverageInsuranceQuery }, data =>
        data
          ? {
              ...data,
              myInsurance: {
                ...data.myInsurance,
                checkStatus: {
                  ...data.myInsurance.checkStatus,
                  state: InsuranceCheckState.UNINSURED,
                },
              },
            }
          : data,
      );
    } else {
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "myInsurance" });
      client.cache.gc();
    }
    return true;
  };
  return { start, resolve };
}
